<template>
    <div
        :style="{
            ...messageColors,
            ...(extendedStyle || {}),
        }"
        class="sc-message--json"
        :class="{ 'right': right }"
    >
        <QuoteMessage :message="message.raw.replyTo" v-if="message.raw.replyTo && !replyMode" />
        
        <!-- JSON Preview -->
        <div class="json-preview-container">
            <div class="json-header">
                <i class="material-icons">code</i>
                <span class="json-title">Desteklenmeyen Mesaj Türü</span>
                <a-button 
                    size="small" 
                    type="link" 
                    @click="openJsonModal"
                    class="view-details-btn"
                >
                    Detayları Gör
                </a-button>
            </div>
            
            <!-- JSON Preview Content -->
            <div class="json-preview-content">
                <div class="json-preview-item" v-for="(value, key) in getPreviewData()" :key="key">
                    <span class="json-key">{{ key }}:</span>
                    <span class="json-value">{{ formatPreviewValue(value) }}</span>
                </div>
                <div v-if="hasMoreData" class="json-more-indicator">
                    ... ve {{ getTotalKeysCount() - 3 }} alan daha
                </div>
            </div>
        </div>

        <!-- Meta bilgileri -->
        <div class="json-meta-container">
            <span
                v-if="message.data.meta"
                :style="{ color: messageColors.color }"
                class="sc-message-meta-new"
                v-tippy="{
                    content: formatDate(message),
                    followCursor: true,
                    placement: 'top',
                }"
            >{{ metaShorter(message.data.meta) }}</span>
        </div>

        <!-- JSON Detail Modal -->
        <Modal2
            v-if="showJsonModal"
            @cancel="showJsonModal = false"
            title="Mesaj Detayları (JSON)"
            :showFooter="false"
            xlarge
        >
            <div class="json-modal-content">
                <v-jsoneditor
                    v-model="jsonData"
                    :options="jsonEditorOptions"
                    height="500px"
                ></v-jsoneditor>
            </div>
        </Modal2>
    </div>
</template>

<script>
import QuoteMessage from "../QuoteMessage.vue";
import Modal2 from "../../common/Modal2.vue";
import VJsoneditor from "v-jsoneditor";
import _ from "lodash";

export default {
    name: "JsonMessage",
    components: {
        QuoteMessage,
        Modal2,
        VJsoneditor,
    },
    props: {
        message: {
            type: Object,
            required: true,
        },
        extendedStyle: {
            type: Object,
        },
        messageColors: {
            type: Object,
            required: true,
        },
        replyMode: {
            type: Boolean,
            default: false,
        },
        right: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showJsonModal: false,
            jsonEditorOptions: {
                mode: "tree",
                modes: ["tree", "code", "view"],
                navigationBar: true,
                mainMenuBar: true,
                statusBar: true,
                language: "tr",
                search: true,
                expandAll: false,
            },
        };
    },
    computed: {
        jsonData() {
            return this.message.raw?.jsonData || {};
        },
        hasMoreData() {
            return this.getTotalKeysCount() > 3;
        },
    },
    methods: {
        formatDate(message) {
            const date = _.get(message, "raw.createdDate", _.get(message, "data.startTimestamp"), null);
            return date
                ? new Date(date).toLocaleString("tr-TR", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : "";
        },
        metaShorter(text) {
            let delimiter = " - ";
            let t = text.split(delimiter);
            if (t[0].length > 11) t[0] = t[0].slice(0, 11) + "..";
            return t.join(delimiter);
        },
        openJsonModal() {
            this.showJsonModal = true;
        },
        getPreviewData() {
            const json = this.jsonData;
            if (!json || typeof json !== 'object') return {};
            
            // İlk 3 key'i al
            const keys = Object.keys(json).slice(0, 3);
            const preview = {};
            keys.forEach(key => {
                preview[key] = json[key];
            });
            
            return preview;
        },
        getTotalKeysCount() {
            const json = this.jsonData;
            return json && typeof json === 'object' ? Object.keys(json).length : 0;
        },
        formatPreviewValue(value) {
            if (value === null) return 'null';
            if (value === undefined) return 'undefined';
            if (typeof value === 'string') {
                return value.length > 30 ? `"${value.substring(0, 30)}..."` : `"${value}"`;
            }
            if (typeof value === 'object') {
                return Array.isArray(value) ? `[${value.length} öğe]` : '{...}';
            }
            return String(value);
        },
    },
};
</script>

<style lang="scss" scoped>
.sc-message--json {
    padding: 12px 15px;
    border-radius: 6px;
    max-width: calc(100% - 120px);
    word-wrap: break-word;
    font: 14px / 20px Helvetica, Arial, sans-serif;
    position: relative;

    .json-preview-container {
        .json-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);

            .material-icons {
                font-size: 18px;
                color: rgba(255, 255, 255, 0.8);
            }

            .json-title {
                font-weight: 600;
                font-size: 13px;
                color: rgba(255, 255, 255, 0.9);
                flex: 1;
            }

            .view-details-btn {
                color: rgba(255, 255, 255, 0.8) !important;
                padding: 2px 8px;
                font-size: 11px;
                height: auto;
                
                &:hover {
                    color: white !important;
                    background: rgba(255, 255, 255, 0.1);
                }
            }
        }

        .json-preview-content {
            .json-preview-item {
                display: flex;
                margin-bottom: 6px;
                font-size: 12px;
                line-height: 1.4;

                .json-key {
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.9);
                    margin-right: 8px;
                    min-width: 60px;
                    font-family: 'Courier New', monospace;
                }

                .json-value {
                    color: rgba(255, 255, 255, 0.8);
                    word-break: break-all;
                    font-family: 'Courier New', monospace;
                }
            }

            .json-more-indicator {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.6);
                font-style: italic;
                margin-top: 8px;
                text-align: center;
            }
        }
    }

    .json-meta-container {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-top: 12px;
        
        .sc-message-meta-new {
            font-size: 8px;
            font-weight: 300;
            color: rgba(255, 255, 255, 0.7);
            text-align: right;
            text-transform: uppercase;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
}

// Received (sol) mesajlar için stil
.sc-message--content.received .sc-message--json {
    color: #263238;
    background-color: #f4f7f9;
    margin-right: 40px;

    .json-header {
        border-bottom-color: rgba(0, 0, 0, 0.1);

        .material-icons {
            color: rgba(0, 0, 0, 0.6);
        }

        .json-title {
            color: rgba(0, 0, 0, 0.8);
        }

        .view-details-btn {
            color: rgba(0, 0, 0, 0.6) !important;
            
            &:hover {
                color: rgba(0, 0, 0, 0.8) !important;
                background: rgba(0, 0, 0, 0.05);
            }
        }
    }

    .json-preview-content {
        .json-preview-item {
            .json-key {
                color: rgba(0, 0, 0, 0.8);
            }

            .json-value {
                color: rgba(0, 0, 0, 0.6);
            }
        }

        .json-more-indicator {
            color: rgba(0, 0, 0, 0.5);
        }
    }

    .sc-message-meta-new {
        color: rgba(0, 0, 0, 0.5) !important;
    }
}

// Sent (sağ) mesajlar için stil - mor arka plan üzerinde okunabilir renkler
.sc-message--json.right {
    .json-header {
        border-bottom-color: rgba(0, 0, 0, 0.2);

        .material-icons {
            color: #2c3e50; // Koyu gri
        }

        .json-title {
            color: #1a1a1a; // Çok koyu gri
            font-weight: 700;
        }

        .view-details-btn {
            color: #2c3e50 !important;
            border: 1px solid rgba(0, 0, 0, 0.2);
            background: rgba(255, 255, 255, 0.1);
            
            &:hover {
                color: #1a1a1a !important;
                background: rgba(255, 255, 255, 0.3);
                border-color: rgba(0, 0, 0, 0.3);
            }
        }
    }

    .json-preview-content {
        .json-preview-item {
            .json-key {
                color: #1a1a1a; // Çok koyu gri - key'ler
                font-weight: 700;
            }

            .json-value {
                color: #2c3e50; // Koyu gri - value'lar
                font-weight: 500;
            }
        }

        .json-more-indicator {
            color: #34495e; // Orta koyu gri
            font-weight: 600;
        }
    }

    .sc-message-meta-new {
        color: #2c3e50 !important;
    }
}
</style>

<style lang="scss">
.json-modal-content {
    min-height: 500px;
    
    .jsoneditor-container {
        min-height: 500px;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
    }
}
</style>
